import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLoanGoal } from 'selectors/getLoanGoal';
import { getApplicationData } from 'selectors/getApplicationData';
import { getStudentLoanData } from 'selectors/getStudentLoanData';
import { getReferralLinkState } from 'selectors/getReferralLink';
import { getFinancialMetricsState } from 'selectors/financialMetrics';
import { getCreditScoreHistoryState } from 'selectors/getCreditScoreHistory';
import { getReferralLink, getFinancialMetrics, getCreditScoreHistory } from 'thunks';
import { VerificationStep } from 'components/Verification/verificationSteps';
import { CreditScoreSource } from 'api/ReportsApi';
import { useAvailableShifts } from 'components/FinancialCheckup/FinancialDashboard/AvailableShiftsSection/useAvailableShifts';
import AvailableShiftsSection from 'components/FinancialCheckup/FinancialDashboard/AvailableShiftsSection';
import ReferralSection from 'components/FinancialCheckup/FinancialDashboard/ReferralSection/ReferralSection';
import DebtTrackerSection from 'components/FinancialCheckup/FinancialDashboard/DebtTrackerSection';
import FinancialMetricsSection from 'components/FinancialCheckup/FinancialDashboard/FinancialMetricsSection';
import CreditScoreSection from 'components/FinancialCheckup/FinancialDashboard/CreditScoreSection';
import ConnectAccountsSection from 'components/FinancialCheckup/FinancialDashboard/ConnectAccountsSection';
import {
  normalizeDebtSummary,
  useFinancialCheckupData,
} from 'components/FinancialCheckup/YourFinances/yourFinancesUtils';
import { DebtSummary, LoanType } from 'handlers/applicationData';
import { getStudentLoanStart } from 'routes/StudentLoanForgivenessRoutes';
import { ReactComponent as StudentLoanIcon } from 'images/fc-student-loan.svg';
import { ReactComponent as ClockIcon } from 'images/calendar-icon.svg';
import { ReactComponent as UpwardTrendIcon } from 'images/icon-growth.svg';
import { ReactComponent as BankIcon } from 'images/bank-icon-small.svg';
import { ReactComponent as LoveIcon } from 'images/love-icon.svg';
import { ReactComponent as DollarIcon } from 'images/dollar-icon.svg';
import { ReactComponent as CreditCardIcon } from 'images/credit-card-icon.svg';

import { DashboardTile, DashboardTileActionType, DashboardTileIconTone, DashboardTileLayout } from './types';

const hasEquifaxHistory = (history: ReturnType<typeof getCreditScoreHistoryState>) =>
  Boolean(history?.some((entry) => entry.source === CreditScoreSource.Equifax));

const hasOutstandingStudentLoans = (debtSummary?: DebtSummary): boolean => {
  if (!debtSummary) return false;
  const studentDebt = debtSummary[LoanType.StudentLoan];
  if ((studentDebt?.totalBalance ?? 0) > 0) return true;
  return (studentDebt?.tradelines ?? []).some((tradeline) => tradeline.balance > 0);
};

export const useDashboardTiles = (): DashboardTile[] => {
  const dispatch = useDispatch();
  const { application } = useSelector(getApplicationData);
  const studentLoanData = useSelector(getStudentLoanData);
  const { shifts } = useAvailableShifts();
  const { plaidConnected, isLoading: isLoanGoalLoading } = useSelector(getLoanGoal);
  const { referralLink, isLoading: isReferralLoading } = useSelector(getReferralLinkState);
  const { data: financialMetricsData } = useSelector(getFinancialMetricsState);
  const creditScoreHistory = useSelector(getCreditScoreHistoryState);
  const { totalDebt } = useFinancialCheckupData();
  const isAccountConnected = Boolean(application?.verificationStepProgress?.[VerificationStep.CheckingAccount]);
  const isConnectTileVisible = !plaidConnected && !isLoanGoalLoading && !isAccountConnected;
  const rawDebtSummary = application?.debtSummary as DebtSummary | string | undefined;
  const debtSummary =
    rawDebtSummary && typeof rawDebtSummary !== 'string' ? normalizeDebtSummary(rawDebtSummary) : undefined;

  useEffect(() => {
    if (!application?.id) return;

    dispatch(getReferralLink(application.id));
    dispatch(getFinancialMetrics(application.id));
    dispatch(getCreditScoreHistory(application.id));
  }, [application?.id, dispatch]);

  return [
    {
      id: 'debtTracker',
      priority: 1,
      visible: Boolean(totalDebt),
      layout: DashboardTileLayout.Full,
      title: 'Debt tracker',
      description: 'See your debts in one place',
      icon: <CreditCardIcon />,
      iconTone: DashboardTileIconTone.Orange,
      analyticsEvent: 'Dashboard Debt Tracker Tile Clicked',
      action: { type: DashboardTileActionType.Sheet, render: () => <DebtTrackerSection /> },
    },
    {
      id: 'shifts',
      priority: 2,
      visible: Boolean(shifts?.length),
      layout: DashboardTileLayout.Shared,
      title: 'Plannery ShiftIQ',
      description: 'Pick up shifts, reach your goals faster',
      icon: <ClockIcon />,
      iconTone: DashboardTileIconTone.Red,
      analyticsEvent: 'Dashboard ShiftIQ Tile Clicked',
      action: {
        type: DashboardTileActionType.Sheet,
        render: (onClose) => <AvailableShiftsSection onClose={onClose} />,
      },
    },
    {
      id: 'connect',
      priority: 3,
      visible: isConnectTileVisible,
      layout: DashboardTileLayout.Shared,
      title: 'Stay on track',
      description: 'Connect accounts, stay on track',
      icon: <BankIcon />,
      iconTone: DashboardTileIconTone.Green,
      analyticsEvent: 'Dashboard Connect Accounts Clicked',
      action: { type: DashboardTileActionType.Sheet, render: () => <ConnectAccountsSection /> },
    },
    {
      id: 'studentLoan',
      priority: 4,
      visible: hasOutstandingStudentLoans(debtSummary),
      layout: DashboardTileLayout.Shared,
      title: 'Lower your student loan payments',
      description: 'See repayment and forgiveness options that may fit your federal loans',
      icon: <StudentLoanIcon />,
      iconTone: DashboardTileIconTone.Blue,
      analyticsEvent: 'Dashboard Student Loan Tile Clicked',
      action: {
        type: DashboardTileActionType.Navigate,
        path: getStudentLoanStart(studentLoanData),
      },
    },
    {
      id: 'referral',
      priority: 5,
      visible: Boolean(referralLink) && !isReferralLoading,
      layout: DashboardTileLayout.Shared,
      highlight: true,
      title: 'Refer a friend',
      description: 'Earn $5 per friend who signs up',
      icon: <LoveIcon />,
      iconTone: DashboardTileIconTone.Purple,
      analyticsEvent: 'Dashboard Referral Tile Clicked',
      action: { type: DashboardTileActionType.Sheet, render: () => <ReferralSection /> },
    },
    {
      id: 'metrics',
      priority: 6,
      visible: Boolean(financialMetricsData?.income && financialMetricsData?.spending),
      layout: DashboardTileLayout.Shared,
      title: 'Your finances',
      description: 'See your income and spending trends',
      icon: <DollarIcon />,
      iconTone: DashboardTileIconTone.Blue,
      analyticsEvent: 'Dashboard Metrics Tile Clicked',
      action: { type: DashboardTileActionType.Sheet, render: () => <FinancialMetricsSection /> },
    },
    {
      id: 'creditScore',
      priority: 7,
      visible: hasEquifaxHistory(creditScoreHistory),
      layout: DashboardTileLayout.Shared,
      title: 'Credit score*',
      description: 'Tap to see your credit score',
      icon: <UpwardTrendIcon />,
      iconTone: DashboardTileIconTone.Green,
      analyticsEvent: 'Dashboard Credit Score Tile Clicked',
      action: { type: DashboardTileActionType.Sheet, render: () => <CreditScoreSection /> },
    },
  ];
};
